import { Badge } from "@component/badge";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Input } from "@component/input";
import ExternalShareWarningDialog from "@container/external-share-dialog";
import { useSnackbar } from "@hook/use-snackbar";
import { JobCreateRequest } from "@service/employer";
import { getCompanyPermissions } from "@service/user";
import { UserPermissionItem } from "@type/company";
import { JobOwnerItem } from "@type/job";
import { isValidEmail } from "@util/string";
import { SearchIcon, UserPlusIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    list_owner: JobOwnerItem[];
    onUpdateData: (field: keyof JobCreateRequest, value: any) => void;
}

export default function DialogMemberJobAccess({ isOpen, onClose, list_owner, onUpdateData }: Props) {
    const snackbar = useSnackbar();
    const [searchQuery, setSearchQuery] = useState("");
    const [showDropdown, setShowDropdown] = useState(true);
    const [listUserWorkspace, setListUserWorkspace] = useState<UserPermissionItem[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<UserPermissionItem[]>([]);
    const [showExternalWarning, setShowExternalWarning] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [showErrorEmail, setShowErrorEmail] = useState<string>("");
    const [pendingExternalOwner, setPendingExternalOwner] =
        useState<JobOwnerItem[]>([]);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            getCompanyPermissions({
                page: 1,
                limit: 10,
            }).then((resp) => {
                if (resp.status === 200) {
                    setListUserWorkspace(resp.data.data.members.map((item: UserPermissionItem) => {
                        if (item.permission !== "invited") {
                            return item;
                        } else {
                            return {
                                ...item,
                                user_id: `invited-${Date.now()}-${item.email}`,
                            }
                        }
                    }));
                }
            })
        }

        return () => {
            setSearchQuery("");
            setShowDropdown(false);
            setSelectedMembers([]);
            setSelectedEmails([]);
        }
    }, [isOpen])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
        }
    };

    const isWorkspaceDomain = (email: string) => {
        const emailDomain = email.split("@")[1];
        const workspaceDomain = "company.com"; // This could be dynamic based on organization settings
        return emailDomain === workspaceDomain;
    };

    const isExternalEmail = (email: string) => {
        return !(listUserWorkspace || []).some((member) => member.email === email && !member.is_external);
    };

    const toggleMemberSelection = (member: UserPermissionItem) => {
        setSelectedMembers((prev) => {
            const isSelected = prev.some((m) => m.user_id === member.user_id);
            if (isSelected) {
                return prev.filter((m) => m.user_id !== member.user_id);
            } else {
                return [...prev, member];
            }
        });
        setShowErrorEmail("");
    };

    const removeEmailExternal = (email: string) => {
        setSelectedEmails((prev) => prev.filter((e) => e !== email));
    }

    const toggleEmailSelection = (email: string) => {
        setShowErrorEmail("");
        // Validate email format first
        if (!isValidEmail(email)) {
            snackbar.showError("Please enter a valid email address.");
            return;
        }

        // Check if email already exists in workspace (joined or invited)
        const existsInWorkspace = (list_owner || []).find(
            (owner) => owner.email.toLowerCase() === email.toLowerCase()
        );

        const selectedEmail = listUserWorkspace.find((member) => member.email.toLowerCase() === email.toLowerCase());
        if (selectedEmail?.permission === "invited") {
            setShowErrorEmail("This member is still invited and has not yet joined the workspace.");
            return;
        }

        if (existsInWorkspace) {
            setShowErrorEmail("This email is already added as a collaborator.");
            return;
        }

        // Check if email is already in current batch
        const existsInBatch = selectedEmails.some(
            (e) => e.toLowerCase() === email.toLowerCase()
        );

        if (existsInBatch) {
            setShowErrorEmail("This email is already selected to be added.");
            return;
        }

        setSelectedEmails((prev) => {
            const isSelected = prev.includes(email);
            if (isSelected) {
                return prev.filter((e) => e !== email);
            } else {
                return [...prev, email];
            }
        });
    };

    const cancelAddExternalOwner = () => {
        setPendingExternalOwner([]);
        setShowExternalWarning(false);
    };


    const handleCloseAddMemberDialog = () => {
        setSearchQuery("");
        setShowDropdown(false);
        setSelectedMembers([]);
        setSelectedEmails([]);
        onClose();
    };

    const confirmAddExternalOwner = () => {
        onUpdateData("listing_owners", [
            ...(list_owner || []),
            ...pendingExternalOwner,
        ]);
        setSearchQuery("");
        setPendingExternalOwner([]);
        setShowExternalWarning(false);
        setShowDropdown(false);
        onClose();
    };

    const addExternalEmail = () => {
        selectedEmails.forEach((email) => {
            if ((list_owner || []).some((o) => o.email === email)) {
                snackbar.showError(`${email} is already added as a collaborator.`);
                return;
            }
        })

        let pendingUsers = selectedEmails.map((email) => ({
            user_id: `external-${Date.now()}-${email}`,
            name: "",
            email: email,
            status: "invited",
            is_external: true,
            is_send_invitation: false,
            joined_at: "",
        }));

        pendingUsers = selectedMembers.map((member) => ({
            user_id: member.user_id,
            name: `${member.first_name} ${member.last_name}`,
            email: member.email,
            status: "invited",
            is_external: false,
            is_send_invitation: false,
            joined_at: "",
        })).concat(pendingUsers);

        if (selectedEmails.length > 0) {
            setPendingExternalOwner(pendingUsers);
            setShowExternalWarning(true);
        } else {
            onUpdateData("listing_owners", [
                ...(list_owner || []),
                ...pendingUsers,
            ]);
            setSearchQuery("");
            setPendingExternalOwner([]);
            setShowExternalWarning(false);
            setShowDropdown(false);
            onClose();
        }
    };

    const filteredMembers = (listUserWorkspace || []).length > 0 ? (listUserWorkspace || []).filter((member) => {
        const query = searchQuery.toLowerCase();
        const alreadyAdded = (list_owner || []).some(
            (o) => o.email === member.email
        );
        return (
            !alreadyAdded && member.permission !== "write" && member.permission !== "owner" && !member.is_external &&
            (member.email.toLowerCase().includes(query))
        );
    }) : [];

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="sm:max-w-[500px]" closeButtonClassName="right-4 top-4">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlusIcon className="h-5 w-5 text-gray-700" />
                            Add Collaborator
                        </DialogTitle>
                        <DialogDescription>
                            Enter an email to add a collaborator to this job.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Search Input */}
                        <div className="relative">
                            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />

                            <Input
                                ref={inputRef}
                                placeholder="Enter email address"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onKeyDown={handleKeyDown}
                                onFocus={() => setShowDropdown(true)}
                                className="pl-9"
                            />
                        </div>

                        {showDropdown && (
                            <div className="border border-border rounded-md max-h-[280px] overflow-y-auto">
                                {/* Workspace Members */}
                                {(filteredMembers || []).filter((member) => member.permission !== "invited").length > 0 && (
                                    <div>
                                        <div className="px-3 py-2 bg-gray-50">
                                            <p className="text-xs font-semibold text-gray-500 uppercase">
                                                Workspace Members
                                            </p>
                                        </div>
                                        {(filteredMembers || []).filter((member) => member.permission !== "invited").map((member) => {
                                            const isSelected = selectedMembers.some(
                                                (m) => m.user_id === member.user_id
                                            );
                                            return (
                                                <div
                                                    key={member.user_id}
                                                    onClick={() => toggleMemberSelection(member)}
                                                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-accent transition-colors ${isSelected ? "bg-blue-50" : ""
                                                        }`}
                                                >
                                                    <div
                                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isSelected
                                                            ? "bg-blue-600 border-blue-600"
                                                            : "border-gray-300"
                                                            }`}
                                                    >
                                                        {isSelected && (
                                                            <svg
                                                                className="w-3 h-3 text-white"
                                                                fill="none"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path d="M5 13l4 4L19 7"></path>
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-xs font-medium">
                                                        {member.first_name ? member.first_name?.charAt(0).toUpperCase() : member.email.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {[member.first_name, member.last_name].join(" ")}
                                                        </p>
                                                        <p className="text-xs text-gray-600 truncate">
                                                            {member.email}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Email Option (Internal or External) */}
                                {(isValidEmail(searchQuery.trim()) || !isExternalEmail(searchQuery.trim())) && (
                                    <div>
                                        {filteredMembers.length > 0 && (
                                            <div className="px-3 py-2 bg-gray-50">
                                                <p className="text-xs font-semibold text-gray-500 uppercase">
                                                    External Users
                                                </p>
                                            </div>
                                        )}
                                        {(() => {
                                            const email = searchQuery.trim();
                                            const isSelected = selectedEmails.includes(email);
                                            return (
                                                <div
                                                    onClick={() => toggleEmailSelection(email)}
                                                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-accent transition-colors ${isSelected ? "bg-blue-50" : ""
                                                        }`}
                                                >
                                                    <div
                                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isSelected
                                                            ? "bg-blue-600 border-blue-600"
                                                            : "border-gray-300"
                                                            }`}
                                                    >
                                                        {isSelected && (
                                                            <svg
                                                                className="w-3 h-3 text-white"
                                                                fill="none"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path d="M5 13l4 4L19 7"></path>
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-sm font-medium">
                                                        {email.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            Invite user
                                                        </p>
                                                        <p className="text-xs text-gray-600 truncate">
                                                            {email}
                                                        </p>
                                                    </div>
                                                    {!isWorkspaceDomain(email) && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-orange-100 text-orange-800 hover:bg-orange-100 shadow-none text-xs"
                                                            style={{ borderRadius: "1rem" }}
                                                        >
                                                            External
                                                        </Badge>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* No Results */}
                                {filteredMembers.length === 0 && !(isValidEmail(searchQuery.trim()) || !isExternalEmail(searchQuery.trim())) && (
                                    <div
                                        className="px-4 py-8 text-sm text-center text-muted-foreground"
                                        style={{ paddingTop: "40px", paddingBottom: "40px" }}
                                    >
                                        <>
                                            No existing members found.
                                            <br />
                                            Enter a valid email to invite users to collaborate.
                                        </>
                                    </div>
                                )}
                            </div>
                        )}
                        {showErrorEmail && (
                            <p className="text-sm text-red-600">
                                {showErrorEmail}
                            </p>
                        )}

                        {/* Selected Members/Emails Indicator */}
                        {(selectedMembers.length > 0 || selectedEmails.length > 0) && (
                            <div
                                className="space-y-2"
                                style={{ paddingTop: "16px", paddingBottom: "16px" }}
                            >
                                <p className="text-sm font-medium text-gray-700">
                                    Selected ({selectedMembers.length + selectedEmails.length})
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {/* Selected Workspace Members */}
                                    {selectedMembers.map((member, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full"
                                        >
                                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-xs font-medium">
                                                {member.first_name ? member.first_name.charAt(0).toUpperCase() : member.email.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="text-sm text-gray-900">
                                                {[member.first_name, member.last_name].join(" ")}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => toggleMemberSelection(member)}
                                                className="ml-1 text-gray-500 hover:text-gray-700"
                                            >
                                                <XIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                    {/* Selected External Emails */}
                                    {selectedEmails.map((email) => (
                                        <div
                                            key={email}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full"
                                        >
                                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-xs font-medium">
                                                {email.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="text-sm text-gray-900">{email}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeEmailExternal(email)}
                                                className="ml-1 text-gray-500 hover:text-gray-700"
                                            >
                                                <XIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-4">
                        <Button variant="outline" onClick={handleCloseAddMemberDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={addExternalEmail}
                            disabled={
                                selectedMembers.length === 0 && selectedEmails.length === 0
                            }
                            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Add Collaborator
                            {selectedMembers.length + selectedEmails.length > 1 ? "s" : ""}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <ExternalShareWarningDialog
                isOpen={showExternalWarning}
                onClose={cancelAddExternalOwner}
                onConfirm={confirmAddExternalOwner}
            />
        </>
    )
}